import React, { useCallback, useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Form,
  Button,
  Typography,
  Space,
  notification,
  Tag,
  Row,
  Col,
  Modal
} from 'antd';
import { Link } from 'react-router-dom';
import TableWithSorter from '../../components/Common/TableWithSorter';
import {
  fetchStartCommunityApplication,
  getCommunities,
  submitStartCommunityApplicationStatus,
  submitManualApplication,
  updateStartCommunityApplication
} from '../../services/communityService';
import { getDaysDifferenceBetweenDates } from '../../utilities/dateHelper';
import CommunitiesApplicationForm from './components/CommunitiesApplicationForm.jsx';
import StartCommunityApplicationModal from './components/StartCommunityApplicationModal.jsx';
import StartCommunityApplication from './components/CommunitiesEnrolment';
import {
  applicationStatus,
  defaultPageSize,
  sortOrderList
} from './constant';
import {
  getCancellationData,
  getformattedApplicationForCSV,
  isInternalEmail
} from './utils';
import Text from 'antd/lib/typography/Text';
import { catchClause } from '@babel/types';

const { Title } = Typography;

const StartCommunityApplicationPage = () => {
  const [form] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [enrollmentForm] = Form.useForm();

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [communityList, setCommunityList] = useState([]);

  const [filters, setFilters] = useState({
    sortOrder: sortOrderList[0].value,
    startDate: null,
    endDate: null,
    sortBy: 'createdAt',
    status: null
  });
  const [applicationList, setApplicationList] = useState(false);
  const [activeApplication, setActiveApplication] = useState(null);

  const [isEnrolmentModalOpen, setIsEnrolmentModalOpen] = useState(false);
  const [csvDataList, setCsvDataList] = useState([]);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [isCsvDataLoading, setIsCsvDataLoading] = useState(false);

  const columns = [
    {
      title: 'No',
      dataIndex: 'key',
      key: 'key',
      render: (value, item, index) => (pageNo - 1) * 10 + index + 1
    },
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => setActiveApplication(record)}>
            {text}
          </Button>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) =>
        renderApplicationStatusTag({
          text,
          status: record?.status
        })
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'is Community Setup?',
      dataIndex: 'isCommunitySetup',
      key: 'isCommunitySetup',
      render: (text, record) => {
        if (!record?.isCommunitySetup)
          return <Tag color="default">Nope</Tag>;

        return <Tag color="default">{record?.community?.code}</Tag>;
      }
    },
    {
      title: 'Tracking data',
      dataIndex: 'trackingData',
      key: 'trackingData',
      render: (text, record) => {
        return record.trackingData?.fromPage;
      }
    },
    {
      title: 'Pending Day(s)',
      dataIndex: 'createdAt',
      key: 'pendingDays',
      render: (_, record) => {
        const isPending = record.status === applicationStatus.PENDING;

        const pendingDays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: record?.createdAt
        });

        return isPending ? `${pendingDays} days` : '-';
      },
      skipInjectedSorter: true,
      sorter: (a, b) => {
        const isAPending = a.status === applicationStatus.PENDING;
        const isBPending = b.status === applicationStatus.PENDING;

        const pendingADays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: a?.createdAt
        });

        const pendingBDays = getDaysDifferenceBetweenDates({
          date1: new Date(),
          date2: b?.createdAt
        });

        const offsetADays = isAPending ? pendingADays : -1;
        const offsetBDays = isBPending ? pendingBDays : -1;

        return offsetADays - offsetBDays;
      }
    },
    {
      title: 'Notes',
      dataIndex: 'reviewerNotes',
      key: 'reviewerNotes',
      width: 400
    },
    {
      title: 'Followers',
      dataIndex: 'totalFollowers',
      key: 'totalFollowers'
    }
  ];

  const renderApplicationStatusTag = ({ status, text }) => {
    const tagColor =
      status === applicationStatus.APPROVE
        ? 'green'
        : status === applicationStatus.REJECT
        ? 'volcano'
        : applicationStatus.MAYBE === status
        ? 'orange'
        : 'blue';
    return <Tag color={tagColor}>{text}</Tag>;
  };

  const onPaginationChange = (data) => {
    setPageNo(data.current);
    setPageSize(data.pageSize);
  };

  const getCommunityApplications = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('');
      const params = {
        pageNo,
        pageSize,
        ...filters
      };

      const { data, error } = await fetchStartCommunityApplication(params);

      if (error) throw new Error(error);

      setApplicationList(data.paginatedList);
      setTotalCount(data.totalCount);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(false);
    }
  }, [pageNo, pageSize, filters]);

  const getCSVDataList = async () => {
    try {
      setIsCsvDataLoading(true);
      const { data, error } = await fetchStartCommunityApplication({
        ...filters
      });

      if (error) throw new Error(error);

      const formattedList = getformattedApplicationForCSV({
        list: data.paginatedList
      });
      setCsvDataList(formattedList);
      setIsCsvModalOpen(true);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsCsvDataLoading(false);
    }
  };

  const onFinish = (values) => {
    setPageNo(1);
    if (
      new Date(values?.startDate).getTime() >
      new Date(values?.endDate).getTime()
    ) {
      notification['error']({
        message: 'Error',
        description: 'End Date cannot be before Start Date'
      });
      return;
    }

    const statusFilter =
      values?.status === applicationStatus.CANCELLED
        ? { subscriptionStatus: values?.status }
        : { status: values?.status };

    const filterData = {
      sortOrder: values.sortOrder ?? sortOrderList[0].value,
      sortBy: 'createdAt',
      applicationSearch: values.applicationSearch,
      startDate: values.startDate ? values.startDate.utc().format() : null,
      endDate: values.endDate ? values.endDate.utc().format() : null,
      platform: values.platform ?? null,
      category: values.category ?? null,
      launchTime: values.launchTime ?? null,
      ...statusFilter
    };

    setFilters(filterData);
  };

  const resetForm = () => {
    form.resetFields();
    form.submit();
  };

  const clearActiveApplication = () => {
    setActiveApplication(null);
  };

  const onStatusSubmit = async ({ status }) => {
    try {
      console.log('submitted to reject??', status);
      setIsStatusLoading(true);
      const params = {
        _id: activeApplication?._id,
        status
      };

      const { data, error } = await submitStartCommunityApplicationStatus(
        params
      );

      if (error) throw new Error(error);

      notification['success']({
        message: 'Success',
        description: `application updated: ${data.data.applicationUpdated}, Email Sent: ${data.data.emailSent}`
      });

      const updatedActiveApplication = { ...activeApplication, status };
      const updatedApplicationList = applicationList?.map(
        (application) => {
          if (application?._id === activeApplication?._id)
            return updatedActiveApplication;

          return application;
        }
      );
      setApplicationList(updatedApplicationList);
      setActiveApplication(updatedActiveApplication);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  const onEnrollmentSubmit = async (values) => {
    try {
      setIsStatusLoading(true);
      const { error } = await submitManualApplication(values);
      if (error) throw new Error(error);
      notification['success']({
        message: 'Success',
        description: `Manual enrolment added!`
      });
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  const updateCommunityApplication = async (values) => {
    try {
      setIsStatusLoading(true);
      const { data, error } = await updateStartCommunityApplication(
        values
      );
      if (error) throw new Error(error);

      notification['success']({
        message: 'Success',
        description: `Application updated!`
      });

      const updatedActiveApplication = data?.data ?? null;
      const updatedApplicationList = applicationList?.map(
        (application) => {
          if (application?._id === activeApplication?._id)
            return {
              ...updatedActiveApplication
            };

          return application;
        }
      );
      setApplicationList(updatedApplicationList);
      setActiveApplication(updatedActiveApplication);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsStatusLoading(false);
    }
  };

  const updateReviewersNote = async () => {
    await updateCommunityApplication({
      _id: activeApplication?._id,
      reviewerNotes: statusForm.getFieldValue('reviewerNotes')
    });
  };

  const updateApplicationField = async (values) => {
    await updateCommunityApplication({
      _id: activeApplication?._id,
      ...values
    });
  };

  const getCommunityList = useCallback(async () => {
    try {
      setIsLoading(true);
      const values = await getCommunities({ pageNo: 1, pageSize: 1000 });
      const result = values?.data?.paginatedList?.map((community) => ({
        value: community?.code,
        text: community?.title
      }));
      const communities = result.sort((communityA, communityB) =>
        communityA?.text?.localeCompare(communityB?.text)
      );
      setCommunityList(communities);
    } catch (error) {
      notification['error']({
        message: 'Error',
        description: `Error: ${error.message}`
      });
    } finally {
      setIsLoading(true);
    }
  }, [form]);

  useEffect(() => {
    if (communityList?.length > 0) {
      getCommunityApplications();
    }
  }, [getCommunityApplications, communityList]);

  useEffect(() => {
    getCommunityList();
  }, [getCommunityList]);

  return (
    <div>
      <div>
        <Row justify="space-between" align="middle">
          <Col>
            <Title>Applications for starting a community</Title>
          </Col>
          <Col>
            <Row gutter={16}>
              <Col>
                <Button
                  type="primary"
                  centered
                  onClick={getCSVDataList}
                  loading={isCsvDataLoading}>
                  Export CSV
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <CommunitiesApplicationForm
          onFinish={onFinish}
          resetForm={resetForm}
          form={form}
          totalCount={totalCount}
          communityList={communityList}
        />
        <TableWithSorter
          tableLayout="auto"
          scroll={{ x: 1000 }}
          expandable={{ defaultExpandAllRows: true }}
          loading={isLoading}
          dataSource={applicationList}
          columns={columns}
          pagination={{
            total: totalCount,
            defaultPageSize: defaultPageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50']
          }}
          onChange={onPaginationChange}
        />
        <StartCommunityApplicationModal
          activeApplication={activeApplication}
          clearActiveApplication={clearActiveApplication}
          onStatusSubmit={onStatusSubmit}
          isStatusLoading={isStatusLoading}
          renderApplicationStatusTag={renderApplicationStatusTag}
          statusForm={statusForm}
          updateReviewersNote={updateReviewersNote}
          updateApplicationField={updateApplicationField}
        />
        <StartCommunityApplication
          isEnrolmentModalOpen={isEnrolmentModalOpen}
          setIsEnrolmentModalOpen={setIsEnrolmentModalOpen}
          isStatusLoading={isStatusLoading}
          enrollmentForm={enrollmentForm}
          onEnrollmentSubmit={onEnrollmentSubmit}
          communityList={communityList}
        />

        {/* <CommunitiesEnrolment
          isEnrolmentModalOpen={isEnrolmentModalOpen}
          setIsEnrolmentModalOpen={setIsEnrolmentModalOpen}
          isStatusLoading={isStatusLoading}
          enrollmentForm={enrollmentForm}
          onEnrollmentSubmit={onEnrollmentSubmit}
          communityList={communityList}
        /> */}
        <Modal
          visible={isCsvModalOpen}
          onCancel={() => setIsCsvModalOpen(false)}
          footer={null}
          centered>
          <Row style={{ marginBottom: '16px' }}>
            <Text strong>
              Note: The filters are applied on the CSV data
            </Text>
          </Row>
          <Row>
            <Button type="primary">
              <CSVLink data={csvDataList} filename="communityApplications">
                Download CSV File
              </CSVLink>
            </Button>
          </Row>
        </Modal>
      </div>
    </div>
  );
};

export default StartCommunityApplicationPage;
